import React, { useState } from 'react';
import { FiAlertCircle, FiX, FiCheckCircle, FiRefreshCw } from 'react-icons/fi';
import { showToast } from '../../utils/toast';
import { dashboardApi } from '../../api';

interface BrokerAccount {
  relationshipId: string;
  brokerAccountId: string | null;
  brokerAccountName: string;
  status: string;
}

interface MasterRelationship {
  master: {
    _id: string;
    name: string;
    email: string;
  };
  brokerAccounts: BrokerAccount[];
}

interface PrimaryBrokerChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedRelationships: string[]) => void;
  activeRelationships: MasterRelationship[];
  newBrokerName: string;
  isLoading?: boolean;
}

const PrimaryBrokerChangeModal: React.FC<PrimaryBrokerChangeModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  activeRelationships,
  newBrokerName,
  isLoading = false
}) => {
  const [selectedRelationships, setSelectedRelationships] = useState<Set<string>>(new Set());

  // if (!isOpen) return null;

  // Initialize selection: select all by default
  React.useEffect(() => {
    if (isOpen && activeRelationships.length > 0) {
      const allIds = new Set<string>();
      activeRelationships.forEach(masterRel => {
        masterRel.brokerAccounts.forEach(acc => {
          allIds.add(acc.relationshipId);
        });
      });
      setSelectedRelationships(allIds);
    }
  }, [isOpen, activeRelationships]);

  const handleToggleAll = () => {
    if (selectedRelationships.size === getAllRelationshipIds().length) {
      setSelectedRelationships(new Set());
    } else {
      setSelectedRelationships(new Set(getAllRelationshipIds()));
    }
  };

  const handleToggleMaster = (masterId: string) => {
    const masterRel = activeRelationships.find(r => r.master._id === masterId);
    if (!masterRel) return;

    const masterRelationshipIds = masterRel.brokerAccounts.map(acc => acc.relationshipId);
    const allSelected = masterRelationshipIds.every(id => selectedRelationships.has(id));

    const newSelection = new Set(selectedRelationships);
    if (allSelected) {
      masterRelationshipIds.forEach(id => newSelection.delete(id));
    } else {
      masterRelationshipIds.forEach(id => newSelection.add(id));
    }
    setSelectedRelationships(newSelection);
  };

  const handleToggleRelationship = (relationshipId: string) => {
    const newSelection = new Set(selectedRelationships);
    if (newSelection.has(relationshipId)) {
      newSelection.delete(relationshipId);
    } else {
      newSelection.add(relationshipId);
    }
    setSelectedRelationships(newSelection);
  };

  const getAllRelationshipIds = (): string[] => {
    const ids: string[] = [];
    activeRelationships.forEach(masterRel => {
      masterRel.brokerAccounts.forEach(acc => {
        ids.push(acc.relationshipId);
      });
    });
    return ids;
  };

  const handleConfirm = () => {
    if (selectedRelationships.size === 0) {
      showToast('info', 'Please select at least one master to continue following, or skip to proceed without following');
      return;
    }
    onConfirm(Array.from(selectedRelationships));
  };

  const handleSkip = () => {
    onConfirm([]);
  };

  const allSelected = selectedRelationships.size === getAllRelationshipIds().length && getAllRelationshipIds().length > 0;
  const someSelected = selectedRelationships.size > 0 && selectedRelationships.size < getAllRelationshipIds().length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-3 sm:p-4">
      <div className="bg-surface rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 p-4 sm:p-6 border-b border-border">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-warning/10 rounded-lg shrink-0">
              <FiAlertCircle className="h-6 w-6 text-warning" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-foreground">Change Primary Broker</h2>
              <p className="text-sm text-muted mt-1">You are currently following these masters</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-elevated rounded-lg transition-colors text-muted hover:text-foreground shrink-0"
            disabled={isLoading}
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="mb-4 p-4 bg-primary/10 border border-primary/30 rounded-lg">
            <p className="text-sm text-foreground break-anywhere">
              <strong>New Primary Broker:</strong> {newBrokerName}
            </p>
            <p className="text-sm text-muted mt-2">
              Do you want to continue following these masters and broker accounts with your new primary broker?
            </p>
          </div>

          {activeRelationships.length === 0 ? (
            <div className="text-center py-8 text-muted">
              <FiCheckCircle className="h-12 w-12 mx-auto mb-3 text-success" />
              <p>You are not currently following any masters.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Select All */}
              <div className="flex items-center gap-3 p-3 bg-surface-elevated rounded-lg border border-border">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = someSelected;
                  }}
                  onChange={handleToggleAll}
                  className="w-5 h-5 text-primary-600 rounded accent-primary"
                  disabled={isLoading}
                />
                <label className="flex-1 cursor-pointer font-semibold text-foreground">
                  Select All ({selectedRelationships.size} of {getAllRelationshipIds().length})
                </label>
              </div>

              {/* Masters List */}
              {activeRelationships.map((masterRel) => {
                const masterRelationshipIds = masterRel.brokerAccounts.map(acc => acc.relationshipId);
                const masterAllSelected = masterRelationshipIds.every(id => selectedRelationships.has(id));
                const masterSomeSelected = masterRelationshipIds.some(id => selectedRelationships.has(id)) && !masterAllSelected;

                return (
                  <div key={masterRel.master._id} className="border border-border rounded-lg overflow-hidden">
                    {/* Master Header */}
                    <div className="flex items-center gap-3 p-4 bg-surface-elevated border-b border-border">
                      <input
                        type="checkbox"
                        checked={masterAllSelected}
                        ref={(input) => {
                          if (input) input.indeterminate = masterSomeSelected;
                        }}
                        onChange={() => handleToggleMaster(masterRel.master._id)}
                        className="w-5 h-5 text-primary-600 rounded accent-primary"
                        disabled={isLoading}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-foreground break-anywhere">{masterRel.master.name}</p>
                        <p className="text-sm text-muted break-anywhere">{masterRel.master.email}</p>
                      </div>
                    </div>

                    {/* Broker Accounts */}
                    <div className="divide-y divide-border">
                      {masterRel.brokerAccounts.map((account) => (
                        <div
                          key={account.relationshipId}
                          className="flex items-center gap-3 p-4 hover:bg-surface-elevated/50 transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedRelationships.has(account.relationshipId)}
                            onChange={() => handleToggleRelationship(account.relationshipId)}
                            className="w-4 h-4 text-primary-600 rounded accent-primary"
                            disabled={isLoading}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground break-anywhere">{account.brokerAccountName}</p>
                            <p className="text-xs text-muted">Status: {account.status}</p>
                          </div>
                          {selectedRelationships.has(account.relationshipId) && (
                            <FiCheckCircle className="h-5 w-5 text-success shrink-0" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 p-4 sm:p-6 border-t border-border bg-surface-elevated">
          <button
            onClick={handleSkip}
            className="w-full sm:w-auto px-4 py-2 text-muted hover:text-foreground font-medium transition-colors disabled:opacity-50"
            disabled={isLoading}
          >
            Skip (Don't Follow)
          </button>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2 border-2 border-border text-foreground font-semibold rounded-lg hover:bg-surface transition-colors disabled:opacity-50"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="w-full sm:w-auto px-6 py-2 bg-primary hover:bg-primary-600 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading || selectedRelationships.size === 0}
            >
              {isLoading ? (
                <>
                  <FiRefreshCw className="h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <FiCheckCircle className="h-4 w-4" />
                  Continue Following ({selectedRelationships.size})
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrimaryBrokerChangeModal;

